import { config } from "@/Common/envConfig";

import { danger, log, warm } from "@/Utils/Debug";
import { isApp } from "@/Utils/Ionic";
import { hasValue } from "@/Utils/Objects";
import { waitFor } from "@/Utils/Promises";
import { isValidString } from "@/Utils/String";

export interface WindowGTM extends Window {
  dataLayer?: unknown[];
}

export const hasDataLayer = () => {
  return hasValue(window) && hasValue((window as WindowGTM).dataLayer);
};

export const addGoogleGTM = () => {
  if (isValidString(config.googleTagManagerId)) {
    attachScript();
    attachIframe();

    waitFor(1).then(() => {
      if (hasDataLayer()) {
        log("Google Tag Manager attached");
      } else {
        danger("Google Tag Manager Error attaching");
      }
    });
  }
};

// +'&gtm_auth=${config.gtmAuth}&gtm_preview=${config.gtmPreview}'

const attachScript = () => {
  const headElement = document.getElementsByTagName("head")[0]!;
  const gtmScript = document.createElement("script");
  const scriptText = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
  new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
  j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
  'https://www.googletagmanager.com/gtm.js?id='+i+dl+'&gtm_auth=${config.gtmAuth}&gtm_preview=${config.gtmPreview}&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer','${config.googleTagManagerId}');`;

  gtmScript.setAttribute("type", "text/javascript");

  const node = document.createTextNode(scriptText);

  gtmScript.appendChild(node);
  headElement.insertBefore(gtmScript, headElement.firstChild);
};

const attachIframe = () => {
  const bodyElement = document.getElementsByTagName("body")[0]!;
  const gtmIframe = document.createElement("noscript");
  const iframeText = `<iframe src="https://www.googletagmanager.com/ns.html?id=${config.googleTagManagerId}&gtm_auth=${config.gtmAuth}&gtm_preview=${config.gtmPreview}&gtm_cookies_win=x" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;

  gtmIframe.appendChild(document.createTextNode(iframeText));
  bodyElement.insertBefore(gtmIframe, bodyElement.firstChild);
};

export enum EventCategory {
  PRODUCTS = "Products",
  BOT = "Bot",
  PAYMENT_REQUEST = "Payment Request",
  CHAT_OPENS = "Chat Opens",
  NO_PURCHASE = "No Purchase",
}

export enum EventAction {
  CHAT_OPENS = "Chat Opens",
  FIRST_CHAT_INTERACTION = "1st Chat Interaction",
  SHOW_PLANS = "Show Plans",
  SHOW_EXTRA_PLANS = "Show Extra Plans",
  SELECT_PLAN = "Select Plan",
  PAYMENT_REQUEST = "Payment Request",
  START_TRIAL = "Start Trial",
  PURCHASE = "Purchase",
  EMAIL_SENT = "Email Sent",
  SEARCH_BAR_PATIENT = "search_bar_patient",
  CLICK_CALENDAR_OPEN = "click_calendar_open",
  CLICK_CALENDAR_SELECT_DATE = "click_calendar_select_date",
  CLICK_TO_CHAT = "click_to_chat",
  CLICK_TO_MENU_MY_CALENDAR = "click_menu_setting_calendar",
  CLICK_TO_MENU_APPOINTMENTS = "click_menu_patient_appointments",
  CLICK_TO_MENU_PATIENT_REFERRAL_CODE = "click_menu_patient_referral_code",
  CLICK_SHARE_CODE_CTA = "click_share_code_cta",
  CLICK_SHARE_CODE_WHATSAPP = "click_share_code_whatsapp",
  CLICK_SHARE_CODE_EMAIL = "click_share_code_email",
}

export enum EventLabel {
  ADD_AND_PAY = "Añadir y Pagar",
  CHAT_OPENS = "Chat Opens",
  EMAIL_SENT = "Email Sent",
}

export enum EventPlatform {
  WEB = "web3",
  ANDROID = "android",
  IOS = "ios",
}

export enum EventTherapyType {
  INDIVIDUAL = "individual",
  COUPLE = "couple",
  UNDER_18 = "undereighteen",
}

export enum EventTestVersion {
  VERSION_A = "version_A",
  VERSION_B = "version_B",
}

export interface GTMEvent {
  eventAction: EventAction;
  eventCategory?: EventCategory;
  eventLabel?: string | EventLabel;
  leadNumber?: string;
  userId?: string;
  eventValue?: number | string;
  userType?: string;
  shareCode?: string;
}

export interface OnboardingGTMEvent {
  event_step: string;
  step_value?: string;
  rn_id: string;
  tc_country: string;
  tc_acquisition_user_type?: "B2B" | "B2C";
  tc_plan_selected?: string;
  tc_platform: EventPlatform;
  uuid?: string;
  tc_user_type: "new" | "returning";
  tc_test_version?: EventTestVersion;
  tc_therapy_type?: EventTherapyType;
  tc_type_plan_selected?: number;
}

export interface TestGTMEvent {
  therapyType: EventTherapyType;
  testVersion: EventTestVersion;
  typePlanSelected?: number;
}

export interface SubscriptionEvent {
  transactionId: string;
  transactionTotal: number;
  transactionProducts: [
    {
      sku: string;
      name: string;
      price: number;
      quantity: 1;
    },
  ];
  leadNumber: string;
  userId: string;
}

export const addGtmEvent = (gtmEvent: GTMEvent | OnboardingGTMEvent) => {
  if (hasDataLayer()) {
    (window as WindowGTM).dataLayer!.push({
      event: "gaEvent",
      ...gtmEvent,
    });

    if (isApp()) {
      warm("GTM", JSON.stringify(gtmEvent));
    } else {
      warm("GTM", gtmEvent);
    }
  }
};

export const subscriptionGaEvent = (subscriptionEvent: SubscriptionEvent) => {
  if (hasDataLayer()) {
    (window as WindowGTM).dataLayer!.push(subscriptionEvent);
  }
};

export const idGAEvent = (userId: string) => {
  if (hasDataLayer()) {
    (window as WindowGTM).dataLayer!.push({
      userId: userId,
    });
  }
};

export const getEventActionMenu = (nameSection: string): EventAction => {
  return EventAction[`CLICK_TO_MENU_${nameSection}` as keyof typeof EventAction];
};

export const getEventActionShareCode = (nameSection: string): EventAction => {
  return EventAction[`CLICK_SHARE_CODE_${nameSection}` as keyof typeof EventAction];
};
